import {
  Colors,
  Divider,
  Flex,
  IconInfo,
  Typography,
  VerticalSpacing,
} from '@banhala/ui-kit';
import { bridge, isNil, isNotNil, localizeNumber } from 'shared/src/utils';
import { BrandIconFreeDelivery } from '../BrandIconFreeDelivery';
import { MaxDiscountPreviewRow } from '../MaxDiscountPreviewRow';
import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import type { GoodsOption } from 'shared/src/api';
import { useGoodsOptionsPricing } from '../../queries/useGoodsOptionsPricing';
import { useGoodsOptionsBrief } from '../../queries/useGoodsOptionsBrief';
import { usePostBatchRedeemCoupon } from '../../queries/usePostBatchRedeemCoupon';
import { useIsBottomSheetGiftMode } from '../../hooks/useIsBottomSheetGiftMode';
import { useLogDownloadCoupon } from '../../hooks/useLogCompleteCouponDownload';
import { useIsAnonymousUser } from 'shared/src/hooks';
import { CouponStateTag, type CouponState } from '../CouponStateTag';
import type { ProductOptionMaxDiscountMode } from '../../../../hooks/useProductOptionMaxDiscountMode';
import { GoodsParamsContext } from '../../../../context/GoodsParamsContext';

const CouponTagView: React.FC<{
  selected: GoodsOption[];
  mode: ProductOptionMaxDiscountMode;
  isAutoRedeeming?: boolean;
}> = props => {
  const isAnonymousUser = useIsAnonymousUser();
  const injected = useContext(GoodsParamsContext);

  const { data: pricing, isLoading } = useGoodsOptionsPricing({
    options: props.selected.map(option => ({
      sno: option.sno,
      count: option.purchase_count ?? 1,
    })),
  });
  const {
    mutateAsync: postBatchRedeemCoupon,
    isPending: isLoadingPostBatchRedeemCoupon,
  } = usePostBatchRedeemCoupon(props.selected);
  const logCouponDownloadComplete = useLogDownloadCoupon();

  const isAllIssued = useMemo(() => {
    if (!pricing) return false;
    return (
      pricing.coupon_list.filter(coupon => coupon.item.is_issued).length ===
      pricing.coupon_list.length
    );
  }, [pricing]);

  // CLICK CART
  // AI FITTING 없는것 확인
  // Android에서
  const onPressRedeemCoupon = useCallback(async () => {
    if (isAnonymousUser) {
      bridge.assertLogin({
        message: '로그인을 해야 이용 할 수 있습니다.',
      });
      return;
    }

    try {
      const downloadableCoupons =
        pricing?.coupon_list.filter(coupon => !coupon.item.is_issued) ?? [];

      if (downloadableCoupons.length === 0) return;

      await postBatchRedeemCoupon();
      //여기서 COMPLETE_COUPON_DOWNLOAD
      downloadableCoupons.forEach(coupon => {
        logCouponDownloadComplete({ coupon: coupon, isDownloadAll: true });
      });
      bridge.toast('쿠폰이 발급되었습니다');
    } catch (error) {
      const ex = error as Error;
      bridge.toast(ex.message);
    }
  }, [
    logCouponDownloadComplete,
    isAnonymousUser,
    postBatchRedeemCoupon,
    pricing?.coupon_list,
  ]);

  useEffect(() => {
    if (isLoading || isNil(pricing)) return;
    if (pricing.coupon_list.length === 0) return;
    if (isAllIssued) return;

    bridge.logAnalyticsEvent({
      name: 'DISPLAY_COUPON',
      analytics: {
        ...pricing.coupon?.logging.analytics,
        SCREEN: injected?.logging ? injected.logging.screen : 'GOODS_DETAIL',
      },
    });
  }, [injected, isAllIssued, isLoading, pricing]);

  const state = useMemo<CouponState>(() => {
    if (isAnonymousUser) return 'anonymous';
    if (
      isLoading ||
      isNil(pricing) ||
      isLoadingPostBatchRedeemCoupon ||
      props.isAutoRedeeming
    ) {
      return 'loading';
    }
    if (pricing.coupon_list.length === 0) return 'none';
    if (isAllIssued) return 'issued';
    return 'downloadable';
  }, [
    isAllIssued,
    isAnonymousUser,
    props.isAutoRedeeming,
    isLoading,
    isLoadingPostBatchRedeemCoupon,
    pricing,
  ]);

  return (
    <CouponStateTag
      state={state}
      couponCount={pricing?.coupon_list.length ?? 0}
      discount={pricing?.discount_amount ?? 0}
      onPressDownload={onPressRedeemCoupon}
      mode={props.mode}
    />
  );
};

export const PricingInfo: React.FC<{
  selected: GoodsOption[];
  mode?: ProductOptionMaxDiscountMode;
  isAutoRedeeming?: boolean;
}> = props => {
  const mode = props.mode ?? 'disabled';
  const isExperimental = mode !== 'disabled';
  const isAnonymousUser = useIsAnonymousUser();
  const { data: pricing } = useGoodsOptionsPricing({
    options: props.selected.map(option => ({
      sno: option.sno,
      count: option.purchase_count ?? 1,
    })),
  });
  const {
    mutateAsync: postBatchRedeemCoupon,
    isPending: isLoadingPostBatchRedeemCoupon,
  } = usePostBatchRedeemCoupon(props.selected);
  const logCouponDownloadComplete = useLogDownloadCoupon();
  const goodsPrice = useMemo(() => {
    return props.selected.reduce(
      (acc, cur) => acc + cur.price * (cur.purchase_count ?? 1),
      0,
    );
  }, [props.selected]);
  const discountPrice = useMemo(() => {
    if (!pricing || pricing.coupon_list.length === 0) return 0;
    if (isExperimental) return pricing.discount_amount;
    const isAllIssued = pricing.coupon_list.every(
      coupon => coupon.item.is_issued,
    );
    return isAllIssued ? pricing.discount_amount : 0;
  }, [pricing, isExperimental]);

  const totalPrice = useMemo(() => {
    return goodsPrice - discountPrice;
  }, [discountPrice, goodsPrice]);

  const isGiftMode = useIsBottomSheetGiftMode();

  const isAllIssued = useMemo(() => {
    if (!pricing || pricing.coupon_list.length === 0) return false;
    return pricing.coupon_list.every(coupon => coupon.item.is_issued);
  }, [pricing]);

  const showPreviewRow =
    mode === 'variantB' &&
    isNotNil(pricing) &&
    pricing.coupon_list.length > 0 &&
    !isAllIssued;
  const showSingleRow =
    isExperimental && isNotNil(pricing) && pricing.coupon_list.length === 0;

  const { data: brief } = useGoodsOptionsBrief();
  const needsSummaryGap =
    isExperimental && !showPreviewRow && (brief?.minimum_order_amount ?? 0) === 0;
  const summaryBottomPadding = needsSummaryGap ? 16 : 0;

  const onPressRedeemPreview = useCallback(async () => {
    if (isAnonymousUser) {
      bridge.assertLogin({ message: '로그인을 해야 이용 할 수 있습니다.' });
      return;
    }
    try {
      const downloadableCoupons =
        pricing?.coupon_list.filter(coupon => !coupon.item.is_issued) ?? [];
      if (downloadableCoupons.length === 0) return;
      await postBatchRedeemCoupon();
      downloadableCoupons.forEach(coupon => {
        logCouponDownloadComplete({ coupon, isDownloadAll: true });
      });
      bridge.toast('쿠폰이 발급되었습니다');
    } catch (error) {
      if (error instanceof Error) bridge.toast(error.message);
    }
  }, [
    isAnonymousUser,
    logCouponDownloadComplete,
    postBatchRedeemCoupon,
    pricing?.coupon_list,
  ]);

  if (props.selected.length === 0) return null;

  if (isGiftMode) {
    return (
      <Flex padding={{ horizontal: 20, vertical: 16 }} gap={12}>
        <Flex.Row alignItems={'center'} gap={4}>
          <IconInfo size={16} fill={Colors.content_secondary} />
          <Typography type={'body2-relaxed'} color={'content_secondary'}>
            모든 선물 상품은 일반 배송으로 부내드려요
          </Typography>
        </Flex.Row>
        <Divider size={'small'} />
        <Flex gap={8}>
          <Flex.Row justifyContent={'space-between'}>
            <Typography type={'subtitle1'}>예상 결제금액</Typography>
            <Typography type={'h4'}>{localizeNumber(totalPrice)}원</Typography>
          </Flex.Row>
        </Flex>
      </Flex>
    );
  }

  const horizontalPadding = isExperimental ? 16 : 20;

  if (showPreviewRow && pricing) {
    return (
      <Flex padding={{ top: 16 }}>
        <Flex padding={{ horizontal: horizontalPadding }}>
          <Flex.Row
            justifyContent={'space-between'}
            height={30}
            alignItems={'center'}
          >
            <Typography type={'body2'} color={'content_tertiary'}>
              총 {props.selected.length}개 상품
            </Typography>
            <Typography type={'h4'} color={'content_primary'}>
              {localizeNumber(goodsPrice)}원
            </Typography>
          </Flex.Row>
        </Flex>
        <Flex margin={{ top: 12 }}>
          <MaxDiscountPreviewRow
            potentialPrice={goodsPrice - pricing.discount_amount}
            loading={isLoadingPostBatchRedeemCoupon}
            onPressRedeem={onPressRedeemPreview}
          />
        </Flex>
      </Flex>
    );
  }

  if (showSingleRow) {
    return (
      <Flex
        padding={{
          horizontal: horizontalPadding,
          top: 16,
          bottom: summaryBottomPadding,
        }}
      >
        <Flex gap={8}>
          <Flex.Row justifyContent={'space-between'}>
            <Typography type={'subtitle1'}>예상 결제금액</Typography>
            <Typography type={'h4'} color={'content_primary'}>
              {localizeNumber(totalPrice)}원
            </Typography>
          </Flex.Row>
          {!isExperimental && (
            <Flex.Row justifyContent={'flex-end'}>
              <BrandIconFreeDelivery />
            </Flex.Row>
          )}
        </Flex>
      </Flex>
    );
  }

  return (
    <Flex
      padding={{
        horizontal: horizontalPadding,
        top: 16,
        bottom: summaryBottomPadding,
      }}
    >
      <Flex.Row
        justifyContent={'space-between'}
        height={30}
        alignItems={'center'}
      >
        <Typography type={'body2'} color={'content_tertiary'}>
          총 {props.selected.length}개 상품
        </Typography>
        <Typography type={'body2'}>{localizeNumber(goodsPrice)}원</Typography>
      </Flex.Row>
      <Flex.Row
        justifyContent={'space-between'}
        height={30}
        alignItems={'center'}
      >
        <Typography type={'body2'} color={'content_tertiary'}>
          쿠폰 할인
        </Typography>
        <CouponTagView
          selected={props.selected}
          mode={mode}
          isAutoRedeeming={props.isAutoRedeeming}
        />
      </Flex.Row>
      <VerticalSpacing size={12} />
      <Divider size={'small'} />
      <VerticalSpacing size={12} />
      <Flex gap={8}>
        <Flex.Row justifyContent={'space-between'}>
          <Typography type={'subtitle1'}>예상 결제금액</Typography>
          <Typography
            type={'h4'}
            color={
              isExperimental && discountPrice > 0
                ? 'content_pink_primary'
                : 'content_primary'
            }
          >
            {localizeNumber(totalPrice)}원
          </Typography>
        </Flex.Row>
        {!isExperimental && (
          <Flex.Row justifyContent={'flex-end'}>
            <BrandIconFreeDelivery />
          </Flex.Row>
        )}
      </Flex>
    </Flex>
  );
};
